// contexts/SellerContext.js
import React, { createContext, useContext, useState, useEffect } from 'react';
import { doc, getDoc } from 'firebase/firestore';
import { db } from '../services/firebase';
import { saveData, loadData, removeData } from '../utils/storage';

const SellerContext = createContext();

export const useSeller = () => {
  return useContext(SellerContext);
};

const SELLER_STORAGE_KEY = 'sellerData';

// 🔄 Registration flags live in Firestore ("profile" collection) - that is the
// source of truth used at login. Cached data can go stale (another device,
// partial writes, older app versions), so re-read them on app start before any
// route guard runs. An incomplete seller must never be treated as complete.
const fetchRegistrationFlags = async (sellerId) => {
  try {
    const snap = await getDoc(doc(db, 'profile', sellerId));
    const profile = snap.exists() ? snap.data() : {};
    return {
      sellerRegistration: !!profile.SellerRegistration,
      segmentRegistration: !!profile.SegmentRegistration,
      productRegistration: !!profile.ProductRegistration,
    };
  } catch (err) {
    // Offline / transient failure: keep whatever is cached instead of
    // incorrectly resetting the seller's progress.
    console.error('[SellerContext] Flag refresh error:', err);
    return null;
  }
};

export const SellerProvider = ({ children }) => {
  const [seller, setSeller] = useState({
    sellerId: null,
    mobile: null,
    segment: null,
    state: null,
    district: null,
    registrationStatus: null,
    sellerRegistration: false,
    segmentRegistration: false,
    productRegistration: false,
    organizationName: '',
    logourl: '',
  });
  const [loading, setLoading] = useState(true);

  // 🔁 Rehydrate seller on app start
  useEffect(() => {
    const initializeSeller = async () => {
      try {
        // Optional: migrate from localStorage (if used before)
        const legacy = localStorage.getItem(SELLER_STORAGE_KEY);
        console.log("legacy", legacy);
        if (legacy) {
          await saveData(SELLER_STORAGE_KEY, JSON.parse(legacy));
          
          localStorage.removeItem(SELLER_STORAGE_KEY);
        }

        const saved = await loadData(SELLER_STORAGE_KEY);
        if (saved) {
          if (saved.sellerId) {
            const flags = await fetchRegistrationFlags(saved.sellerId);
            if (flags) {
              setSeller({ ...saved, ...flags });
            } else {
              setSeller(saved);
            }
          } else {
            setSeller(saved);
          }
        }
      } catch (err) {
        console.error('[SellerContext] Init error:', err);
      } finally {
        setLoading(false); // ✅ critical
      }
    };

    initializeSeller();
  }, []);

  // 💾 Auto-save when seller changes
  useEffect(() => {
    if (seller?.sellerId) {
      saveData(SELLER_STORAGE_KEY, { ...seller, lastUpdated: Date.now() });
    } else {
      removeData(SELLER_STORAGE_KEY);
    }
  }, [seller]);

  const updateSeller = (sellerData) => {
    setSeller(prev => ({ ...prev, ...sellerData }));
  };

  const clearSeller = async () => {
    setSeller({
      sellerId: null,
      mobile: null,
      segment: null,
      state: null,
      district: null,
      registrationStatus: null,
      sellerRegistration: false,
      segmentRegistration: false,
      productRegistration: false,
      organizationName: '',
      logourl: '',
    });
    await removeData(SELLER_STORAGE_KEY);
  };

  return (
    <SellerContext.Provider value={{ seller, updateSeller, clearSeller, loading }}>
      {children}
    </SellerContext.Provider>
  );
};